import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {Button} from 'reactstrap';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './tour-schedule.reducer';

export const TourSchedule = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const tourScheduleList = useAppSelector(state => state.tourSchedule.entities);
  const loading = useAppSelector(state => state.tourSchedule.loading);
  const totalItems = useAppSelector(state => state.tourSchedule.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="tour-schedule-heading" data-cy="TourScheduleHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.home.title">Tour Schedules</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" onClick={handleSyncList} disabled={loading} variant="outlined" startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}>
            <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button component={Link} to="/tour-schedule/new" variant="contained" color="primary" id="jh-create-entity" data-cy="entityCreateButton" startIcon={<FontAwesomeIcon icon="plus" />}>
            <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.home.createLabel">Create new Tour Schedule</Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {tourScheduleList && tourScheduleList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('startDatetime')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.startDatetime">Start Datetime</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('startDatetime')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('noPassengers')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.noPassengers">No Passengers</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('noPassengers')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('noKids')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.noKids">No Kids</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('noKids')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('noPets')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.noPets">No Pets</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('noPets')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.createdBy">Created By</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.tour">Tour</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.vehicle">Vehicle</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.driver">Driver</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tourScheduleList.map((tourSchedule, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/tour-schedule/${tourSchedule.id}`} variant="text" size="small">
                        {tourSchedule.id}
                      </Button>
                    </TableCell>
                    <TableCell>{tourSchedule.code}</TableCell>
                    <TableCell>{tourSchedule.startDatetime ? <TextFormat type="date" value={tourSchedule.startDatetime} format={APP_LOCAL_DATE_FORMAT} /> : null}</TableCell>
                    <TableCell>{tourSchedule.noPassengers}</TableCell>
                    <TableCell>{tourSchedule.noKids}</TableCell>
                    <TableCell>{tourSchedule.noPets}</TableCell>
                    <TableCell>{tourSchedule.createdDate ? <TextFormat type="date" value={tourSchedule.createdDate} format={APP_LOCAL_DATE_FORMAT} /> : null}</TableCell>
                    <TableCell>{tourSchedule.createdBy ? tourSchedule.createdBy.login : ''}</TableCell>
                    <TableCell>{tourSchedule.tour ? <Link to={`/tour/${tourSchedule.tour.id}`}>{tourSchedule.tour.code}</Link> : ''}</TableCell>
                    <TableCell>{tourSchedule.vehicle ? <Link to={`/vehicle/${tourSchedule.vehicle.id}`}>{tourSchedule.vehicle.plate}</Link> : ''}</TableCell>
                    <TableCell>{tourSchedule.driver ? <Link to={`/driver/${tourSchedule.driver.id}`}>{tourSchedule.driver.name}</Link> : ''}</TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button component={Link} to={`/tour-schedule/${tourSchedule.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`} color="primary" startIcon={<FontAwesomeIcon icon="pencil-alt" />}>
                        </Button>
                        <Button onClick={() => (window.location.href = `/tour-schedule/${tourSchedule.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)} color="error" startIcon={<FontAwesomeIcon icon="trash" />}>
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.tourSchedule.home.notFound">No Tour Schedules found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tourScheduleList && tourScheduleList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default TourSchedule;
